import { useState, useEffect, useCallback } from "react";
import { tr } from "@/utils/i18n";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { SubscriptionTab } from "@/components/dashboard/SubscriptionTab";
import { ReferralsTab } from "@/components/dashboard/ReferralsTab";
import { AnalyticsTab } from "@/components/dashboard/AnalyticsTab";
import { ReorderReminderBanner } from "@/components/dashboard/ReorderReminderBanner";
import api from "@/utils/api";
import {
  ArrowLeft,
  Bell,
  Calendar,
  Crown,
  DollarSign,
  FlaskConical,
  Gift,
  History,
  LineChart,
  LogOut,
  Plus,
  Syringe,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();

  const DAYS_OF_WEEK = language === 'pt'
    ? ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"]
    : language === 'es'
    ? ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"]
    : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const [cycles, setCycles] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('dashboardTab') || 'cycles');

  const [newReminder, setNewReminder] = useState({
    cycle_id: "",
    peptide_name: "",
    dosage: "",
    time: "08:00",
    days: [],
    enabled: true
  });
  const [showReminderDialog, setShowReminderDialog] = useState(false);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const headers = getAuthHeader();

      const [cyclesRes, remindersRes, statsRes, subRes, refRes] = await Promise.all([
        api.get('/my-cycles', { headers }),
        api.get('/reminders', { headers }),
        api.get('/affiliate/stats', { headers }),
        api.get('/subscription/status', { headers }).catch(() => ({ data: null })),
        api.get('/referral/stats', { headers }).catch(() => ({ data: null }))
      ]);

      setCycles(cyclesRes.data);
      setReminders(remindersRes.data);
      setAffiliateStats(statsRes.data);
      setSubscriptionStatus(subRes.data);
      setReferralStats(refRes.data);
    } catch {
      toast.error("Failed to load dashboard data");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, fetchData]);

  const copyReferralLink = () => {
    if (referralStats?.referral_link) {
      navigator.clipboard.writeText(referralStats.referral_link);
      toast.success(tr(language, 'Link copied!', 'Link copiado!', '¡Enlace copiado!'));
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) return;

    setSendingInvite(true);
    try {
      await api.post('/referral/invite', { email: inviteEmail }, { headers: getAuthHeader() });
      toast.success(tr(language, 'Invite sent!', 'Convite enviado!', '¡Invitación enviada!'));
      setInviteEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleDeleteCycle = async (cycleId) => {
    try {
      await api.delete(`/cycles/${cycleId}`, { headers: getAuthHeader() });
      setCycles(cycles.filter(c => c.id !== cycleId));
      toast.success("Cycle deleted");
    } catch {
      toast.error("Failed to delete cycle");
    }
  };

  const handleToggleReminder = async (reminderId, enabled) => {
    try {
      const reminder = reminders.find(r => r.id === reminderId);
      await api.put(`/reminders/${reminderId}`, {
        ...reminder,
        enabled
      }, { headers: getAuthHeader() });

      setReminders(reminders.map(r =>
        r.id === reminderId ? { ...r, enabled } : r
      ));
      toast.success(enabled ? "Reminder enabled" : "Reminder disabled");
    } catch {
      toast.error("Failed to update reminder");
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await api.delete(`/reminders/${reminderId}`, { headers: getAuthHeader() });
      setReminders(reminders.filter(r => r.id !== reminderId));
      toast.success("Reminder deleted");
    } catch {
      toast.error("Failed to delete reminder");
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.peptide_name || !newReminder.dosage || newReminder.days.length === 0) {
      toast.error("Please fill in all fields and select at least one day");
      return;
    }

    try {
      await api.post('/reminders', newReminder, { headers: getAuthHeader() });
      toast.success("Reminder created!");
      setShowReminderDialog(false);
      setNewReminder({
        cycle_id: "",
        peptide_name: "",
        dosage: "",
        time: "08:00",
        days: [],
        enabled: true
      });
      fetchData();
    } catch {
      toast.error("Failed to create reminder");
    }
  };

  const toggleDay = (day) => {
    if (newReminder.days.includes(day)) {
      setNewReminder({ ...newReminder, days: newReminder.days.filter(d => d !== day) });
    } else {
      setNewReminder({ ...newReminder, days: [...newReminder.days, day] });
    }
  };

  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        toast.success(tr(language, "Notifications enabled!", "Notificações ativadas!", "¡Notificaciones activadas!"));
      } else {
        toast.error(tr(language, "Notifications blocked. Please enable in browser settings.", "Notificações bloqueadas. Ative nas configurações do navegador.", "Notificaciones bloqueadas. Habilite en configuración del navegador."));
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] grid-pattern">
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
                data-testid="back-home"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">{t("dashboard")}</h1>
                <p className="text-sm text-zinc-400">{t("welcomeBack")}, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button
                variant="ghost"
                onClick={requestNotificationPermission}
                className="text-zinc-400 hover:text-white"
                data-testid="enable-notifications"
              >
                <Bell className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => { logout(); navigate("/"); }}
                className="text-zinc-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {(subscriptionStatus?.is_paused || subscriptionStatus?.is_cancelled) && (
          <div
            className={`mb-6 p-4 rounded-xl border flex flex-col sm:flex-row items-start sm:items-center gap-3 ${
              subscriptionStatus.is_paused
                ? 'bg-amber-500/10 border-amber-500/40'
                : 'bg-red-500/10 border-red-500/40'
            }`}
            data-testid="reactivate-reminder"
          >
            <div className="flex-1">
              <p className={`text-sm font-semibold ${subscriptionStatus.is_paused ? 'text-amber-300' : 'text-red-300'}`}>
                {subscriptionStatus.is_paused
                  ? (language === 'pt' ? 'Sua assinatura está pausada' : language === 'es' ? 'Tu suscripción está pausada' : 'Your membership is paused')
                  : (language === 'pt' ? 'Sua assinatura foi cancelada' : language === 'es' ? 'Tu suscripción fue cancelada' : 'Your membership is cancelled')}
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                {language === 'pt'
                  ? 'Reative para restaurar 30% de desconto Summit + benefícios Premium.'
                  : language === 'es'
                    ? 'Reactiva para restaurar 30% descuento Summit + beneficios Premium.'
                    : 'Reactivate to restore 30% Summit discount + all Premium benefits.'}
              </p>
            </div>
            <button
              onClick={() => {
                setActiveTab('subscription');
                localStorage.setItem('dashboardTab', 'subscription');
              }}
              className={`px-4 py-2 rounded-lg font-semibold text-sm shrink-0 ${
                subscriptionStatus.is_paused
                  ? 'bg-amber-500 hover:bg-amber-400 text-black'
                  : 'bg-red-500 hover:bg-red-400 text-white'
              }`}
              data-testid="reactivate-reminder-cta"
            >
              {language === 'pt' ? 'Reativar Assinatura' : language === 'es' ? 'Reactivar Suscripción' : 'Reactivate Membership'}
            </button>
          </div>
        )}

        <ReorderReminderBanner getAuthHeader={getAuthHeader} language={language} />

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); localStorage.setItem('dashboardTab', v); }} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1 flex-wrap">
            <TabsTrigger value="cycles" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-cycles">
              <History className="w-4 h-4 mr-2" />
              {t("savedCycles")}
            </TabsTrigger>
            <TabsTrigger value="subscription" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-subscription">
              <Crown className="w-4 h-4 mr-2" />
              {tr(language, 'Subscription', 'Assinatura', 'Suscripción')}
            </TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-referrals">
              <Gift className="w-4 h-4 mr-2" />
              {tr(language, 'Referrals', 'Indicações', 'Referencias')}
            </TabsTrigger>
            <TabsTrigger value="reminders" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-reminders">
              <Bell className="w-4 h-4 mr-2" />
              {t("injectionReminders")}
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-analytics">
              <LineChart className="w-4 h-4 mr-2" />
              {t("affiliateAnalytics")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subscription">
            <SubscriptionTab
              subscriptionStatus={subscriptionStatus}
              language={language}
              navigate={navigate}
              getAuthHeader={getAuthHeader}
              onRefresh={() => fetchData({ silent: true })}
            />
          </TabsContent>

          <TabsContent value="referrals">
            <ReferralsTab
              referralStats={referralStats}
              language={language}
              inviteEmail={inviteEmail}
              setInviteEmail={setInviteEmail}
              sendingInvite={sendingInvite}
              handleSendInvite={handleSendInvite}
              copyReferralLink={copyReferralLink}
            />
          </TabsContent>

          <TabsContent value="cycles" className="space-y-6" data-testid="cycles-content">
            {cycles.length === 0 ? (
              <Card className="bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <FlaskConical className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{t("noCycles")}</h3>
                  <p className="text-zinc-400 mb-6">
                    {tr(language, "Generate your first peptide cycle to get started", "Gere seu primeiro ciclo de peptídeos para começar", "Genere su primer ciclo de péptidos para comenzar")}
                  </p>
                  <Button onClick={() => navigate("/")} className="btn-primary">
                    {tr(language, 'Create Cycle', 'Criar Ciclo', 'Crear Ciclo')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {cycles.map((cycle) => (
                  <Card key={cycle.id} className="bg-zinc-950/50 border-zinc-800 hover:border-cyan-500/30 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-white">{cycle.goal}</h3>
                            <Badge className={
                              cycle.status === "active" ? "badge-green" :
                              cycle.status === "completed" ? "badge-violet" : "badge-cyan"
                            }>
                              {cycle.status || (tr(language, 'planned', 'planejado', 'planificado'))}
                            </Badge>
                          </div>
                          <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{cycle.summary}</p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="text-zinc-400">
                              <FlaskConical className="w-4 h-4 inline mr-1" />
                              {cycle.peptides?.length || 0} peptides
                            </span>
                            <span className="text-zinc-400">
                              <Calendar className="w-4 h-4 inline mr-1" />
                              {cycle.total_duration_weeks} {t("weeks")}
                            </span>
                            <span className="text-green-400">
                              <DollarSign className="w-4 h-4 inline mr-1" />
                              ${cycle.total_cost_usd}
                            </span>
                            <span className="text-zinc-500">
                              {tr(language, 'Created:', 'Criado:', 'Creado:')} {format(new Date(cycle.created_at), "MMM d, yyyy")}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              localStorage.setItem("cycleResults", JSON.stringify(cycle));
                              navigate("/results");
                            }}
                            className="border-zinc-700"
                            data-testid={`view-cycle-${cycle.id}`}
                          >
                            {tr(language, 'View', 'Ver', 'Ver')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteCycle(cycle.id)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`delete-cycle-${cycle.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reminders" className="space-y-6" data-testid="reminders-content">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">{t("injectionReminders")}</h2>
              <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-reminder-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t("addReminder")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-950 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="text-white">
                      {tr(language, 'Create Reminder', 'Criar Lembrete', 'Crear Recordatorio')}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t("peptideName")}</Label>
                      <Input
                        placeholder="e.g., BPC-157"
                        value={newReminder.peptide_name}
                        onChange={(e) => setNewReminder({ ...newReminder, peptide_name: e.target.value })}
                        className="bg-zinc-900 border-zinc-800"
                        data-testid="reminder-peptide"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t("dosage")}</Label>
                      <Input
                        placeholder="e.g., 0.25mg"
                        value={newReminder.dosage}
                        onChange={(e) => setNewReminder({ ...newReminder, dosage: e.target.value })}
                        className="bg-zinc-900 border-zinc-800"
                        data-testid="reminder-dosage"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t("time")}</Label>
                      <Input
                        type="time"
                        value={newReminder.time}
                        onChange={(e) => setNewReminder({ ...newReminder, time: e.target.value })}
                        className="bg-zinc-900 border-zinc-800"
                        data-testid="reminder-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t("days")}</Label>
                      <div className="flex flex-wrap gap-2">
                        {DAYS_OF_WEEK.map((day) => (
                          <Button
                            key={day}
                            type="button"
                            variant={newReminder.days.includes(day) ? "default" : "outline"}
                            size="sm"
                            onClick={() => toggleDay(day)}
                            className={newReminder.days.includes(day) ? "bg-cyan-500 text-black" : "border-zinc-700"}
                          >
                            {day.slice(0, 3)}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <Button onClick={handleCreateReminder} className="w-full btn-primary" data-testid="save-reminder">
                      {tr(language, 'Create Reminder', 'Criar Lembrete', 'Crear Recordatorio')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {reminders.length === 0 ? (
              <Card className="bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <Bell className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{t("noReminders")}</h3>
                  <p className="text-zinc-400">
                    {tr(language, "Create reminders to never miss an injection", "Crie lembretes para nunca perder uma injeção", "Cree recordatorios para nunca perder una inyección")}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {reminders.map((reminder) => (
                  <Card key={reminder.id} className="bg-zinc-950/50 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            reminder.enabled ? "bg-cyan-500/20" : "bg-zinc-800"
                          }`}>
                            <Syringe className={`w-6 h-6 ${reminder.enabled ? "text-cyan-400" : "text-zinc-600"}`} />
                          </div>
                          <div>
                            <h4 className="text-white font-medium">{reminder.peptide_name}</h4>
                            <p className="text-sm text-zinc-400">
                              {reminder.dosage} {tr(language, 'at', 'às', 'a las')} {reminder.time}
                            </p>
                            <div className="flex gap-1 mt-1">
                              {reminder.days.map((day) => (
                                <Badge key={day} variant="outline" className="text-xs border-zinc-700">
                                  {day.slice(0, 3)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Switch
                            checked={reminder.enabled}
                            onCheckedChange={(checked) => handleToggleReminder(reminder.id, checked)}
                            data-testid={`toggle-reminder-${reminder.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteReminder(reminder.id)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`delete-reminder-${reminder.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab
              affiliateStats={affiliateStats}
              language={language}
              t={t}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
